import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { Shield } from 'lucide-react';
import Header from './components/Header';
import StuckLoaderBanner from './components/StuckLoaderBanner';
import AppTeaserBanner from './components/AppTeaserBanner';
// Lazy loading: ogni pagina viene scaricata solo quando l'utente ci naviga
// Riduce il bundle iniziale del ~50% → app più veloce al primo caricamento
const HomePage      = React.lazy(() => import('./pages/HomePage'));
const ResultsPage   = React.lazy(() => import('./pages/ResultsPage'));
const DealsPage     = React.lazy(() => import('./pages/DealsPage'));
const NewestPage    = React.lazy(() => import('./pages/NewestPage'));
const SavedPage     = React.lazy(() => import('./pages/SavedPage'));
const NotFoundPage  = React.lazy(() => import('./pages/NotFoundPage'));
const ListingRedirectPage = React.lazy(() => import('./pages/ListingRedirectPage'));
const AuctionsPage  = React.lazy(() => import('./pages/AuctionsPage'));
const ProSuccessPage = React.lazy(() => import('./pages/ProSuccessPage'));
const FeaturedPage  = React.lazy(() => import('./pages/FeaturedPage'));
const ProPage       = React.lazy(() => import('./pages/ProPage'));
const ProAnalyticsPage = React.lazy(() => import('./pages/ProAnalyticsPage'));
const AssistantPage = React.lazy(() => import('./pages/AssistantPage'));
const AdminMonitorPage = React.lazy(() => import('./pages/AdminMonitorPage'));
const AuthCallback  = React.lazy(() => import('./pages/AuthCallback'));
const MagicLinkPage = React.lazy(() => import('./pages/MagicLinkPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const SchedulerHealthPage = React.lazy(() => import('./pages/SchedulerHealthPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const FeedbackPage = React.lazy(() => import('./pages/FeedbackPage'));
const ValorePage = React.lazy(() => import('./pages/ValorePage'));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage'));
const PreviewCardsPage = React.lazy(() => import('./pages/PreviewCardsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const ChangelogPage = React.lazy(() => import('./pages/ChangelogPage'));
import { AuthProvider } from './context/AuthContext';
import { CountryProvider } from './context/CountryContext';
import { CountryConfirmDialog } from './components/CountryConfirmDialog';
import { trackEvent } from './lib/analytics';
import { AlertsProvider } from './context/AlertsContext';
import { BrowsingProvider } from './context/BrowsingContext';
import { ProUpsellProvider } from './context/ProUpsellContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { CookieBanner, CookieSettingsLink } from './components/CookieBanner';
import { RecentlyViewedBar } from './components/RecentlyViewedBar';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ScrollToTop } from './components/ScrollToTop';
import { LoginNudge } from './components/LoginNudge';
import { PushNotificationBanner } from './components/PushNotificationBanner';
import './lib/i18n';
import './App.css';

// Loading screen creativa con logo ScanMarket
function PageLoader() {
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center gap-6">
      <div className="relative">
        {/* Pulse ring */}
        <div className="absolute inset-0 rounded-full border-2 border-red-500/20 animate-ping" style={{width:64,height:64,margin:'auto',top:0,bottom:0,left:0,right:0}} />
        {/* Logo icon */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center shadow-lg shadow-red-500/20">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <span className="text-lg font-bold tracking-tight">
          <span className="text-red-500">Scan</span>
          <span className="text-foreground">Market</span>
          <span className="text-muted-foreground">.ch</span>
        </span>
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-bounce" style={{animationDelay:'0ms'}} />
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-bounce" style={{animationDelay:'150ms'}} />
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-bounce" style={{animationDelay:'300ms'}} />
        </div>
      </div>
    </div>
  );
}

// Redirect / to the user's language homepage
// Priority: explicit user choice > Swiss browser language (de/fr/it) > default DE
function HomeRedirect() {
  // Only honor localStorage if user explicitly chose a language (not auto-detected)
  const explicit = typeof window !== 'undefined' ? localStorage.getItem('scanmarket_lang_chosen') : null;
  if (explicit) {
    const stored = localStorage.getItem('scanmarket_language');
    if (stored && ['de', 'fr', 'it', 'en'].includes(stored)) {
      return <Navigate to={`/${stored}`} replace />;
    }
  }
  // Check ALL browser languages — find the first Swiss language in the list
  const browserLangs = (navigator.languages || [navigator.language || '']).map(l => l.split('-')[0]);
  const swissLang = browserLangs.find(l => ['de', 'fr', 'it'].includes(l));
  if (swissLang) {
    return <Navigate to={`/${swissLang}`} replace />;
  }
  // Default: German (largest Swiss market, 63%)
  return <Navigate to="/de" replace />;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const currentQuery = searchParams.get('q') || '';

  // Detect Google in-app browser (GSA on iOS, ;wv on Android) — applica safe-mode CSS
  // per evitare gradient/blur che rendono male nella WebView Google App.
  useEffect(() => {
    try {
      const ua = navigator.userAgent || '';
      const isGoogleApp = /\bGSA\/|;\s*wv\)|GoogleApp|Googlebot/i.test(ua);
      if (isGoogleApp) {
        document.documentElement.classList.add('gsa-safemode');
      }
    } catch {}
  }, []);

  // Force mobile rendering on physical phones, even when the user (or browser)
  // enabled "Request Desktop Site" mode (Samsung Internet, Chrome Android, Safari iOS).
  // In quei casi il browser sovrascrive il viewport meta a ~980px facendo collassare
  // l'UI mobile su layout desktop. Detect: pointer:coarse + screen.width < 1024.
  // Fix: forziamo dinamicamente il viewport a width=device-width.
  useEffect(() => {
    try {
      const isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      const isSmallScreen = (window.screen && window.screen.width && window.screen.width < 1024);
      if (!isTouch || !isSmallScreen) return;

      let meta = document.querySelector('meta[name="viewport"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'viewport');
        document.head.appendChild(meta);
      }
      const FORCED = 'width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover';
      meta.setAttribute('content', FORCED);

      // Browser-mode "desktop site" può ri-sovrascrivere viewport dopo il mount.
      // Riapplica una seconda volta dopo 250ms + osserva mutazioni.
      const reapply = () => {
        if (meta.getAttribute('content') !== FORCED) {
          meta.setAttribute('content', FORCED);
        }
      };
      const t1 = setTimeout(reapply, 250);
      const t2 = setTimeout(reapply, 1500);
      document.documentElement.classList.add('force-mobile');
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } catch {}
  }, []);

  // Stale tab guard: Safari iOS / Google App freezano JS execution sulle tab in
  // background. Quando l'utente torna sulla tab, i fetch in pending non si completano
  // più → skeleton infiniti. Soluzione: al ritorno della visibilità, se ci sono
  // skeleton ancora attivi dopo 2s, ricarica la pagina + traccia l'evento via PostHog.
  useEffect(() => {
    let lastHeartbeat = Date.now();
    const heartbeatId = setInterval(() => {
      const now = Date.now();
      const gap = now - lastHeartbeat;
      lastHeartbeat = now;
      if (gap > 2 * 60 * 1000) {
        const stuckSkeleton = document.querySelector('main .animate-pulse:not([data-live-pulse]), main [data-stale-loader]');
        if (stuckSkeleton) {
          trackEvent('tab_resumed_with_stuck_skeleton', { gap_ms: gap, source: 'heartbeat' });
          window.location.reload();
        } else {
          trackEvent('tab_resumed_clean', { gap_ms: gap });
          window.dispatchEvent(new CustomEvent('scanmarket:tab-resumed', { detail: { gapMs: gap } }));
        }
      }
    }, 30000);

    const onVisibility = () => {
      if (!document.hidden) {
        setTimeout(() => {
          const stuckSkeleton = document.querySelector('main .animate-pulse:not([data-live-pulse]), main [data-stale-loader]');
          if (stuckSkeleton) {
            trackEvent('tab_resumed_with_stuck_skeleton', { source: 'visibilitychange' });
            window.location.reload();
          }
        }, 2000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(heartbeatId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Loading-too-long watchdog: traccia quando lo skeleton resta visibile >10s al primo
  // load (problema NON dovuto a tab freeze ma a fetch fallita silenziosamente o bundle
  // bug). Ci dà visibility sui casi reali in produzione.
  useEffect(() => {
    const checkAt = setTimeout(() => {
      const stuckSkeleton = document.querySelector('main .animate-pulse:not([data-live-pulse]), main [data-stale-loader]');
      if (stuckSkeleton) {
        trackEvent('initial_load_stuck_skeleton_10s', {
          path: window.location.pathname,
          query: window.location.search,
        });
      }
    }, 10000);
    return () => clearTimeout(checkAt);
  }, [location.pathname, location.search]);

  const handleSearch = async (query) => {
    const q = (query || '').trim();
    if (!q) return;
    // Nuova ricerca = categoria ricalcolata: "auto" → Auto, poi "kare" non
    // deve restare bloccata su Auto (zero risultati).
    const { navigateWithInferredCategory } = await import('./lib/searchNav');
    await navigateWithInferredCategory(navigate, q);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Rileva session_id nel fragment PRIMA del render normale (evita race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="app-container">
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
      <Header searchQuery={currentQuery} onSearch={handleSearch} />
      <AppTeaserBanner />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/de" element={<HomePage forceLang="de" />} />
          <Route path="/fr" element={<HomePage forceLang="fr" />} />
          <Route path="/it" element={<HomePage forceLang="it" />} />
          <Route path="/en" element={<HomePage forceLang="en" />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/deals" element={<DealsPage />} />
          <Route path="/newest" element={<NewestPage />} />
          <Route path="/auctions" element={<AuctionsPage />} />
          <Route path="/c/:canonical" element={<CategoryPage />} />
          <Route path="/de/c/:canonical" element={<CategoryPage />} />
          <Route path="/fr/c/:canonical" element={<CategoryPage />} />
          <Route path="/it/c/:canonical" element={<CategoryPage />} />
          <Route path="/en/c/:canonical" element={<CategoryPage />} />
          <Route path="/come-funziona" element={<HowItWorksPage />} />
          <Route path="/wie-es-funktioniert" element={<HowItWorksPage />} />
          <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/pro/success" element={<ProSuccessPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/magic" element={<MagicLinkPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/scheduler" element={<SchedulerHealthPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/featured" element={<FeaturedPage />} />
          <Route path="/pro" element={<ProPage />} />
          <Route path="/pro/analytics" element={<ProAnalyticsPage />} />
          <Route path="/assistant" element={<AssistantPage />} />
          <Route path="/admin/monitor" element={<AdminMonitorPage />} />
          <Route path="/saved" element={<SavedPage />} />
          <Route path="/l/:source/:listingId" element={<ListingRedirectPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/preview/cards" element={<PreviewCardsPage />} />
          <Route path="/valore" element={<ValorePage />} />
          <Route path="/valore/:slug" element={<ValorePage />} />
          <Route path="/it/valore" element={<ValorePage />} />
          <Route path="/it/valore/:slug" element={<ValorePage />} />
          <Route path="/de/wert" element={<ValorePage />} />
          <Route path="/de/wert/:slug" element={<ValorePage />} />
          <Route path="/fr/valeur" element={<ValorePage />} />
          <Route path="/fr/valeur/:slug" element={<ValorePage />} />
          <Route path="/en/value" element={<ValorePage />} />
          <Route path="/en/value/:slug" element={<ValorePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <StuckLoaderBanner />
      <footer className="border-t border-border/50 py-6 mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center sm:justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <img src="/favicon.png" alt="ScanMarket" className="w-5 h-5 object-contain" />
            <span>ScanMarket.ch — {t('footerSlogan')}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-center sm:justify-end">
            <span className="hidden sm:inline">{t('footerDataFrom')} Tutti.ch, Anibis.ch, Ricardo.ch, Shpock, eBay.ch</span>
            <span className="hidden sm:inline">·</span>
            <a href="/feedback" data-testid="footer-feedback-link" className="hover:text-foreground transition-colors">{t('footerFeedback') || 'Feedback'}</a>
            <span>·</span>
            <a href={i18n.language?.startsWith('de') ? '/wie-es-funktioniert' : i18n.language?.startsWith('fr') ? '/comment-ca-marche' : i18n.language?.startsWith('en') ? '/how-it-works' : '/come-funziona'} data-testid="footer-how-link" className="hover:text-foreground transition-colors">{t('footerHowItWorks') || 'Come funziona'}</a>
            <span>·</span>
            <a href="/changelog" data-testid="footer-changelog-link" className="hover:text-foreground transition-colors">{t('footerChangelog') || 'Novità'}</a>
            <span>·</span>
            <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-foreground transition-colors">{t('footerPrivacy') || 'Privacy'}</a>
            <span>·</span>
            <CookieSettingsLink />
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{t('footerDisclaimer')}</span>
          </div>
        </div>
        {/* Mobile-only: data sources + disclaimer (hidden on desktop where they're inline) */}
        <div className="sm:hidden max-w-6xl mx-auto px-4 mt-3 text-center text-[11px] text-muted-foreground/80 space-y-1">
          <p>{t('footerDataFrom')} Tutti.ch, Anibis.ch, Ricardo.ch, Shpock, eBay.ch</p>
          <p>{t('footerDisclaimer')}</p>
        </div>
        {/* Data transparency widget (LPD/GDPR trust signal) */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-3 pt-3 border-t border-border/30" data-testid="footer-data-transparency">
          <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground/70">
            <Shield size={10} className="text-emerald-500/80" />
            <span>{t('footerDataTransparency')}</span>
          </div>
        </div>
      </footer>
      <Toaster position="bottom-right" richColors />
      <LoginNudge />
      <PushNotificationBanner />
      <RecentlyViewedBar />
      {/* Feedback floating button — hidden on /feedback page.
          Inline-style su bg/color/border per evitare che il catch-all
          `[class*="bg-zinc-9"]` in gsa-safemode lo renda bianco, e per
          neutralizzare lo smart-invert del WebView Google App / Chrome iOS. */}
      {location.pathname !== '/feedback' && (
      <a href="/feedback" data-testid="feedback-fab"
        className="fixed bottom-2 left-3 z-40 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium shadow-lg hover:scale-105 transition-transform"
        style={{
          background: 'var(--fab-bg)',
          color: 'var(--fab-fg)',
          border: '1px solid var(--fab-border)',
          forcedColorAdjust: 'none',
          WebkitForcedColorAdjust: 'none',
        }}
        title={t('footerFeedback') || 'Feedback'}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        Feedback
      </a>
      )}
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <GoogleOAuthProvider clientId="1023872273470-mmdddj1odg8tfmjch34shcrps2urgqri.apps.googleusercontent.com">
    <BrowserRouter>
      <CountryProvider>
      <AuthProvider>
        <AlertsProvider>
          <BrowsingProvider>
            <ProUpsellProvider>
              <CookieConsentProvider>
                <AppContent />
                <CookieBanner />
                <CountryConfirmDialog />
              </CookieConsentProvider>
            </ProUpsellProvider>
          </BrowsingProvider>
        </AlertsProvider>
      </AuthProvider>
      </CountryProvider>
    </BrowserRouter>
    </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
